import { Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxSpinnerService } from 'ngx-spinner';

/**
 * Keeps the full-screen spinner from trapping a dialog underneath it.
 *
 * ngx-spinner renders at `z-index: 99999` with an opaque backdrop that swallows pointer events,
 * while the CDK overlay a dialog opens sits at `1000`: a dialog underneath is invisible *and*
 * unclickable, and `disableClose` then removes every way out. A dead end, not a race.
 *
 * Which spinners are up is tracked from `spinnerObservable` rather than assumed, so a named one is
 * suspended too. That only works from the moment this service exists, which is why `AppComponent`
 * injects it: constructing it lazily would miss whatever was already on screen.
 */
@Injectable({ providedIn: 'root' })
export class SpinnerOverlayGuardService {
  private readonly spinner = inject(NgxSpinnerService);

  private readonly visible = new Set<string>();

  constructor() {
    // Torn down with the injector, which for a root service means the tab: there is no consumer
    // whose destruction should stop the tracking, and a test that destroys the TestBed gets a
    // service that stops listening with it.
    this.spinner.spinnerObservable.pipe(takeUntilDestroyed()).subscribe(state => {
      if (!state?.name) return;
      if (state.show) {
        this.visible.add(state.name);
      } else {
        this.visible.delete(state.name);
      }
    });
  }

  /**
   * Take every spinner down for a dialog that owns the screen from here on.
   *
   * One-way, unlike `suspendAround`: whatever was loading is now behind a modal the user has to
   * answer, and putting the spinner back over the page they are returned to would be wrong. A
   * later `hide()` from the operation's own `finally` is a harmless no-op.
   */
  dismissAll(): void {
    for (const name of [...this.visible]) {
      void this.spinner.hide(name);
    }
  }

  /**
   * Run `work` with the spinners down, restoring exactly those that were up.
   *
   * For a question asked in the middle of an operation that still owns the spinner. Restoring
   * leaves the caller's own `finally` owning when it comes down for good.
   */
  async suspendAround<T>(work: () => Promise<T>): Promise<T> {
    const suspended = [...this.visible];
    for (const name of suspended) {
      await this.spinner.hide(name);
    }
    try {
      return await work();
    } finally {
      for (const name of suspended) {
        void this.spinner.show(name);
      }
    }
  }
}
